import {
  createSlice,
  configureStore,
  createAsyncThunk,
  PayloadAction,
} from "@reduxjs/toolkit";
import {
  transformStringDate,
} from "helpers/date-utils";
import { useDispatch } from "react-redux";
import { setCookie } from "react-use-cookie";
import {
  InitResponse,
  init,
  getAvailableCars,
} from "services/rentals-thin.service";

export const getAvilableCarsAction = createAsyncThunk(
  "cars/getAvailable",
  async (params: { startDate: string; endDate: string }) => {
    const response = await getAvailableCars(params.startDate, params.endDate);
    return response.data;
  }
);

export const setInitRentalAction = createAsyncThunk("rental/init", async () => {
  const response: InitResponse = await init();
  setCookie("rentalId", response.data.rentalId, { days: 1 });
  return response.data;
});

export interface Car {
  id: string;
  plateNumber: string;
  serialNumber: string;
  color: string;
  mileage: number;
  model: string;
  brand: string;
  manufacturingYear: number;
  transmission: string;
  engineType: string;
  octanes: string;
  fuelType: string;
  serviceRange: number;
  tireSize: number;
  passengers: number;
  cylinders: number;
  doors: number;
  pricePerDay: number;
  agencyId: string;
  status: string;
  bags: number;
  images: string[];
  agency: Agency;
}

export interface Agency {
    agencyId: string;
    name: string;
}

export interface Rental {
  rentalId: string;
  startDate: string;
  endDate: string;
  totals: Totals;
  contactInfo: ContactInfo;
  delivery?: Delivery;
  payment: Payment;
  confirmation: ConfirmationDetails;
}

export interface Payment {
  method: PaymentMethod;
}

export enum PaymentMethod {
  CASH = "cash",
  TRANSFER = "transfer",
}

export interface Delivery {
  deliveryPointId: string;
  deliveryPoint?: DeliveryPoint;
}

export interface DeliveryPoint {
  id: string;
  name: string;
  active: boolean;
  address: {
    country: string;
    region: string;
    town: string;
    streetName: string;
    zipCode: string;
    apartmentNumber?: string | null;
    neighborhood?: string | null;
    addressLine2?: string | null;
    phoneNumber?: string | null;
    locationUrl?: string | null;
  };
}

export interface ContactInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  birthDate: string;
  dlCheck?: boolean;
}

export interface ItemDetail {
  id: string;
  name: string;
  price: number;
  depositFee?: number;
}

export interface Totals {
  items: ItemDetail[];
  days: number;
  amountDue: number;
}

export interface ConfirmationDetails {
  confirmationCode: string;
  bankDetails?: {
    bankAccountHolder: string;
    bankName: string;
    bankAccountNumber: string;
  };
}

export interface NavigationState {
  currentPath: string;
}

export interface ActionButtonState {
  disabled: boolean;
  isLoading: boolean;
}

const initialState: Rental = {
  rentalId: "",
  startDate: "",
  endDate: "",
  totals: {
    items: [],
    days: 0,
    amountDue: 0,
  },
  contactInfo: {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    birthDate: "",
  },
  delivery: undefined as any,
  payment: {
    method: PaymentMethod.CASH,
  },
  confirmation: {
    confirmationCode: "",
  },
};

export interface GlobalState {
  rental: Rental;
  actionButton: ActionButtonState;
  navigation: NavigationState;
  availableCars: Car[];
}

const initialGlobalState: GlobalState = {
  availableCars: [{
    "id": "17626e15-8c3d-4e4c-b0a0-82f7a81cfc13",
    "color": null,
    "model": "March",
    "brand": "Nissan",
    "manufacturingYear": 2017,
    "transmission": "Estándar",
    "passengers": 5,
    "pricePerDay": 450,
    "bags": 2,
    "images": ["https://tourish-rentals-user-uploads.s3.amazonaws.com/fd9e8e9f-4da5-4979-bcf5-01ea95995c89/17626e15-8c3d-4e4c-b0a0-82f7a81cfc13/1?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Date=20260915T221209Z&X-Amz-SignedHeaders=host&X-Amz-Credential=AKIA5OLHL5Y4KM3AZLEN%2F20260915%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Expires=600&X-Amz-Signature=f8ab0717ab8c885d8c28f6b06d8e5a9434db656a0d2b0f07a8f79068445e4ace"],
    "agency": {
        "agencyId": "ag_9d7e03de58d4",
        "name": "Rentadora 41",
        "address": null,
        "bankDetails": null
    }
}] as any,
  rental: initialState,
  actionButton: { disabled: true, isLoading: false },
  navigation: { currentPath: "" },
};

const rentalSlice = createSlice({
  name: "global",
  initialState: initialGlobalState,
  reducers: {
    enableButton(state: GlobalState) {
      state.actionButton.disabled = false;
    },
    disableButton(state: GlobalState) {
      state.actionButton.disabled = true;
    },
    isLoadingButton(state: GlobalState, action: PayloadAction<boolean>) {
      state.actionButton.isLoading = action.payload;
    },
    setNavigation(state: GlobalState, action: PayloadAction<string>) {
      state.navigation.currentPath = action.payload;
    },
    updateRental(state: GlobalState, action: PayloadAction<Partial<Rental>>) {
      state.rental = { ...state.rental, ...action.payload };
    },
    updateTotals(state: GlobalState, action: PayloadAction<Totals>) {
      state.rental.totals = action.payload;
    },
    updateContactInfo(state: GlobalState, action: PayloadAction<ContactInfo>) {
      state.rental.contactInfo = {
        ...action.payload,
        birthDate: transformStringDate(
          action.payload.birthDate,
          "MM-DD-YYYY",
          "DD/MM/YYYY"
        ),
      };
    },
    updateConfirmationDetails(
      state: GlobalState,
      action: PayloadAction<ConfirmationDetails>
    ) {
      state.rental.confirmation = action.payload;
    },
    updateDelivery(state: GlobalState, action: PayloadAction<Delivery | undefined>) {
      state.rental.delivery = action.payload;
    },
    updatePayment(state: GlobalState, action: PayloadAction<Payment>) {
      state.rental.payment = action.payload;
    },
    updateAvailableCars(state: GlobalState, action: PayloadAction<Car[]>) {
      state.availableCars = action.payload;
    },
    updateDates(
      state: GlobalState,
      action: PayloadAction<{ startDate: string; endDate: string }>
    ) {
      state.rental.startDate = action.payload.startDate;
      state.rental.endDate = action.payload.endDate;
    },
  },
  extraReducers(builder) {
    builder.addCase(getAvilableCarsAction.fulfilled, (state, action) => {
      state.availableCars = action.payload;
    });
    builder.addCase(setInitRentalAction.fulfilled, (state, action) => {
      state.rental = action.payload;
    });
  },
});

export const store = configureStore({
  reducer: rentalSlice.reducer,
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export const {
  enableButton,
  disableButton,
  isLoadingButton,
  setNavigation,
  updateRental,
  updateTotals,
  updateConfirmationDetails,
  updateContactInfo,
  updateDelivery,
  updatePayment,
  updateAvailableCars,
  updateDates,
} = rentalSlice.actions;
export const useAppDispatch: () => AppDispatch = useDispatch;
